import { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { getEmpresa, getProyeccionResultado } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Sigma } from "lucide-react";
import { cn, formatEur } from "@/lib/utils";
import ResultadosProyeccionContent from "@/components/proyeccion/ResultadosProyeccionContent";
import { TABLA_ANIOS, ColsAnios, COL_ANIO_MONO } from "@/components/ui/tabla-anios";

// RESUMEN (petición Raúl 29-09): primer elemento de ANÁLISIS — todas las
// magnitudes clave (Activo, PN, Pasivo, Ventas, EBITDA, Resultado, Cash
// Flow, Tesorería) de TODOS los años, históricos Y proyectados, en una sola
// vista, con marca clara de qué es real y qué es proyección. FUENTE ÚNICA:
// el consolidado de proyección (mismos números que Drivers → Resultados,
// cuadre garantizado por los 3 checks del modelo). Debajo, el detalle
// completo (PyG, balance y flujos) reutilizando ResultadosProyeccionContent.
export default function ResumenAnalisisPage() {
  const { empresaId } = useParams();
  const [empresa, setEmpresa] = useState(null);
  const [resultado, setResultado] = useState(null);
  const [loading, setLoading] = useState(true);

  const cargar = useCallback(async () => {
    try {
      const [emp, res] = await Promise.all([
        getEmpresa(empresaId),
        getProyeccionResultado(empresaId),
      ]);
      setEmpresa(emp.data);
      setResultado(res.data);
    } catch {
      toast.error("Error al cargar el resumen");
    } finally {
      setLoading(false);
    }
  }, [empresaId]);

  useEffect(() => {
    setLoading(true);
    cargar();
  }, [cargar]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  const aniosHist = resultado?.anios_historicos || [];
  const aniosProy = resultado?.anios_proyectados || [];
  const anios = [...aniosHist, ...aniosProy];
  const esProy = (a) => aniosProy.includes(a);

  // Totales del balance del modelo: suma de partidas principales (las filas
  // `sub` — CAPEX acumulado, resultado del ejercicio… — son desglose
  // informativo y no suman para no duplicar).
  const partidas = resultado?.balance?.partidas || [];
  const sumaGrupo = (grupo, a) =>
    partidas
      .filter((p) => p.grupo === grupo && !p.sub)
      .reduce((acc, p) => acc + (p.por_anio?.[String(a)] ?? 0), 0);
  const partidaPorConcepto = (concepto, a) =>
    partidas.find((p) => p.concepto === concepto)?.por_anio?.[String(a)];
  const filaPyg = (concepto, a) =>
    (resultado?.pyg_completo || []).find((f) => f.concepto === concepto)
      ?.por_anio?.[String(a)];
  const kpi = (nombre, a) => resultado?.kpis?.[nombre]?.por_anio?.[String(a)];

  const tesoreria = (a) => partidaPorConcepto("Tesorería", a);
  const cashFlow = (a) => {
    const i = anios.indexOf(a);
    if (i <= 0) return null;
    const act = tesoreria(a);
    const prev = tesoreria(anios[i - 1]);
    if (act == null || prev == null) return null;
    return act - prev;
  };

  const filas = [
    { l: "Ventas", g: (a) => kpi("ventas", a), hint: "Importe neto de la cifra de negocios" },
    { l: "EBITDA", g: (a) => kpi("ebitda", a) },
    { l: "Resultado Neto", g: (a) => filaPyg("Resultado neto", a), tone: "strong" },
    { sep: "BALANCE" },
    { l: "Total Activo", g: (a) => sumaGrupo("activo", a), tone: "strong" },
    { l: "Patrimonio Neto", g: (a) => partidaPorConcepto("Patrimonio neto", a) },
    {
      l: "Pasivo total (exigible)",
      g: (a) => {
        const pnp = sumaGrupo("pasivo_pn", a);
        const pn = partidaPorConcepto("Patrimonio neto", a);
        return pnp == null || pn == null ? null : pnp - pn;
      },
    },
    {
      l: "Deuda financiera (LP + CP)",
      g: (a) =>
        (partidaPorConcepto("Deuda financiera LP", a) ?? 0) +
        (partidaPorConcepto("Deuda financiera CP", a) ?? 0),
    },
    { sep: "CAJA" },
    { l: "Tesorería final", g: tesoreria, tone: "strong" },
    { l: "Cash Flow (Δ tesorería)", g: cashFlow, hint: "Variación de la tesorería frente al año anterior (= cash flow total del modelo)" },
  ];

  const fmt = (v) => (v == null || isNaN(v) ? "—" : formatEur(v));

  return (
    <div className="space-y-6 animate-fade-in" data-testid="resumen-analisis">
      <div>
        <h1 className="page-title flex items-center gap-2">
          <Sigma className="h-6 w-6 text-primary" /> Resumen — todos los años
        </h1>
        <p className="text-sm text-muted-foreground">
          {empresa?.nombre} · magnitudes clave del histórico y de la proyección en una sola
          vista. <span className="text-muted-foreground font-semibold">Gris = real</span> ·{" "}
          <span className="text-proy font-semibold">amarillo "Proy." = proyectado</span>. Misma
          fuente que Drivers → Resultados: los estados cuadran por construcción.
        </p>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle>Magnitudes clave</CardTitle>
        </CardHeader>
        <CardContent className="p-0 overflow-x-auto">
          {/* Misma rejilla que las tablas de Resultados que van debajo */}
          <Table className={cn(TABLA_ANIOS, "text-sm")}>
            <ColsAnios n={anios.length} anio={COL_ANIO_MONO} />
            <TableHeader>
              <TableRow className="bg-muted/40">
                <TableHead>Concepto</TableHead>
                {anios.map((a) => (
                  <TableHead key={a} className={cn("text-right whitespace-nowrap", esProy(a) && "text-proy")}>
                    {a}
                    <span className="block text-[10px] font-normal opacity-70">
                      {esProy(a) ? "Proy." : "Hist."}
                    </span>
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {filas.map((fila, i) =>
                fila.sep ? (
                  <TableRow key={`s-${i}`} className="bg-primary/10">
                    <TableCell colSpan={anios.length + 1}
                      className="font-semibold text-[11px] uppercase tracking-wide text-primary py-1">
                      {fila.sep}
                    </TableCell>
                  </TableRow>
                ) : (
                  <TableRow key={fila.l} className={cn("hover:bg-muted/20", fila.tone === "strong" && "font-semibold bg-primary/5")}>
                    <TableCell title={fila.hint} className="truncate">{fila.l}</TableCell>
                    {anios.map((a) => (
                      <TableCell key={a}
                        className={cn("text-right font-mono tabular-nums whitespace-nowrap",
                          esProy(a) && "bg-proy text-proy")}>
                        {fmt(fila.g(a))}
                      </TableCell>
                    ))}
                  </TableRow>
                )
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Detalle completo: PyG, balance, flujos y salud — la misma vista
          consolidada de Drivers → Resultados, sin duplicar ni un cálculo. */}
      <ResultadosProyeccionContent empresaId={empresaId} readOnly />
    </div>
  );
}
